import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  CircularProgress,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RateReviewIcon from "@mui/icons-material/RateReview";
import SyncProblemIcon from "@mui/icons-material/SyncProblem";
import { PERSONNEL_QUALIFICATIONS, PERSONNEL_ROLES, PERSONNEL_STATUSES } from "../constants";
import PersonnelDialog from "../components/PersonnelDialog";
import { exportPersonnel } from "../exportExcel";
import { API_BASE_URL } from "../config";
import { useMe } from "../MeContext";
import { fmtDateTime } from "../personnelChanges";

const API = `${API_BASE_URL}/personnel`;
const EVENTS_API = `${API_BASE_URL}/events`;
const ATT_API = `${API_BASE_URL}/attendances`;

// Local YYYY-MM-DD (avoids UTC off-by-one from toISOString).
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function Personnel() {
  const { can } = useMe();
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true); // true until the first personnel fetch resolves
  const [editing, setEditing] = useState(null); // null | "new" | id — the record in PersonnelDialog
  const [nameFilter, setNameFilter] = useState("");
  const [qualFilter, setQualFilter] = useState([]);   // qualifications to filter on (OR within)
  const [roleFilter, setRoleFilter] = useState([]);   // roles to filter on (OR within)
  const [statusFilter, setStatusFilter] = useState([]); // statuses to filter on (OR within)
  const [eventFilter, setEventFilter] = useState([]); // future-event ids to filter on (OR within)
  const [events, setEvents] = useState([]); // all events, for the future-events filter options
  const [allAttendances, setAllAttendances] = useState([]); // every attendance, for the list's future-events column
  // Records people have changed themselves from My details and nobody has yet
  // reviewed, by person id (core/personnel_self_edit.py).
  const canReview = can("Personnel.reviewChanges");
  const [pendingById, setPendingById] = useState({});
  const [pendingOnly, setPendingOnly] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios.get(API + "/").then(res => setPeople(res.data)).finally(() => setLoading(false));
    axios.get(EVENTS_API + "/").then(res => setEvents(res.data));
    loadAllAttendances();
    loadPending();
    // Deep-link support: /personnel?edit=<id> opens that record. The dialog loads
    // it itself, so this needn't wait for the list.
    const editId = Number(searchParams.get("edit"));
    if (Number.isInteger(editId) && editId > 0) {
      setEditing(editId);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllAttendances = () =>
    axios.get(ATT_API + "/").then(res => setAllAttendances(res.data));

  const loadPending = () => {
    if (!canReview) return;
    axios.get(`${API}/changes/pending`)
      .then(res => setPendingById(Object.fromEntries(res.data.map(c => [c.personnel_id, c]))));
  };
  const pendingCount = people.filter(p => pendingById[p.id]).length;

  // Future events (not yet finished) each person is linked to, by person id.
  const today = todayISO();
  const futureEventsByPerson = {};
  allAttendances.forEach(a => {
    const ev = a.event;
    if (ev && ev.end_date && ev.end_date >= today) {
      (futureEventsByPerson[a.personnel_id] = futureEventsByPerson[a.personnel_id] || []).push(ev);
    }
  });

  const qualOptions = PERSONNEL_QUALIFICATIONS.map(q => ({ value: q, label: q }));
  const roleOptions = PERSONNEL_ROLES.map(r => ({ value: r, label: r }));
  const statusOptions = PERSONNEL_STATUSES.map(s => ({ value: s.value, label: s.value }));
  // Future events (not yet finished) available as filter options.
  const eventOptions = events
    .filter(ev => ev.end_date && ev.end_date >= today)
    .slice()
    .sort((a, b) => a.end_date.localeCompare(b.end_date))
    .map(ev => ({ value: ev.id, label: ev.label_short || ev.label_long }));

  // Name is a substring match; each multi-select narrows to people matching ANY of its values.
  const filtered = people.filter(p => {
    const nameOk = `${p.forename} ${p.surname}`.toLowerCase().includes(nameFilter.toLowerCase());
    const quals = p.qualifications || [];
    const qualOk = qualFilter.length === 0 || qualFilter.some(q => quals.includes(q));
    const roles = p.roles || [];
    const roleOk = roleFilter.length === 0 || roleFilter.some(r => roles.includes(r));
    const statusOk = statusFilter.length === 0 || statusFilter.includes(p.status);
    const personEventIds = (futureEventsByPerson[p.id] || []).map(ev => ev.id);
    const eventOk = eventFilter.length === 0 || eventFilter.some(id => personEventIds.includes(id));
    const pendingOk = !pendingOnly || !!pendingById[p.id];
    return nameOk && statusOk && qualOk && roleOk && eventOk && pendingOk;
  });

  // Display labels for a person's qualifications, expanding "Other" to its free text.
  const qualLabels = (p) => {
    const qs = p.qualifications || [];
    return qs.map(q => (q === "Other" && p.qualifications_other ? `Other: ${p.qualifications_other}` : q));
  };

  const futureEventCell = (personId) => {
    const evs = (futureEventsByPerson[personId] || []).slice().sort((x, y) => x.end_date.localeCompare(y.end_date));
    if (evs.length === 0) return "—";
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {evs.map(ev => <Chip key={ev.id} label={ev.label_short} size="small" color="primary" variant="outlined" />)}
      </Box>
    );
  };

  // A multi-select chip filter. `options` are {value, label}; selecting none = no filter.
  const filterSelect = (label, value, setValue, options) => (
    <TextField
      label={label} select size="small" sx={{ minWidth: 200 }}
      value={value}
      onChange={e => {
        const v = typeof e.target.value === "string" ? e.target.value.split(",") : e.target.value;
        setValue(v);
      }}
      slotProps={{
        inputLabel: { shrink: true },
        select: {
          multiple: true,
          displayEmpty: true,
          renderValue: (selected) => (
            selected.length === 0
              ? <Typography variant="body2" color="text.secondary">Any</Typography>
              : (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map(v => {
                    const o = options.find(x => x.value === v);
                    return <Chip key={v} label={o ? o.label : v} size="small" />;
                  })}
                </Box>
              )
          ),
        },
      }}
    >
      {options.length === 0 && <MenuItem disabled><em>None</em></MenuItem>}
      {options.map(o => (
        <MenuItem key={o.value} value={o.value}>
          <Checkbox checked={value.indexOf(o.value) > -1} />
          <ListItemText primary={o.label} />
        </MenuItem>
      ))}
    </TextField>
  );

  const anyFilterActive = statusFilter.length > 0 || qualFilter.length > 0 || roleFilter.length > 0 || eventFilter.length > 0
    || nameFilter !== "" || pendingOnly;
  const clearFilters = () => {
    setNameFilter(""); setStatusFilter([]); setQualFilter([]); setRoleFilter([]); setEventFilter([]); setPendingOnly(false);
  };

  // The dialog saves its own record; the list just takes the result back.
  const upsertPerson = (person) =>
    setPeople(ps => (ps.some(p => p.id === person.id)
      ? ps.map(p => (p.id === person.id ? person : p))
      : [...ps, person]));

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">Personnel</Typography>
        <Stack direction="row" spacing={1}>
          {can("Personnel.syncCheck") && (
            <Button variant="outlined" startIcon={<SyncProblemIcon />}
                    onClick={() => navigate("/personnel-sync")}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              Check MailChimp &amp; O365
            </Button>
          )}
          {can("Personnel.export") && (
            <Button variant="outlined" startIcon={<FileDownloadIcon />}
                    disabled={filtered.length === 0}
                    onClick={() => exportPersonnel(filtered)}
                    sx={{ display: { xs: "none", sm: "inline-flex" } }}>
              Export to Excel
            </Button>
          )}
          {can("Personnel.add") && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing("new")}>
              Add personnel
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Filter */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Filter by name" size="small" value={nameFilter}
                   onChange={e => setNameFilter(e.target.value)} />
        {filterSelect("Status", statusFilter, setStatusFilter, statusOptions)}
        {filterSelect("Qualifications", qualFilter, setQualFilter, qualOptions)}
        {filterSelect("Shelter Stone role", roleFilter, setRoleFilter, roleOptions)}
        {filterSelect("Future events", eventFilter, setEventFilter, eventOptions)}
        {canReview && (pendingCount > 0 || pendingOnly) && (
          <Button size="small" color="info" variant={pendingOnly ? "contained" : "outlined"}
                  startIcon={<RateReviewIcon />} onClick={() => setPendingOnly(v => !v)}>
            Changes to review ({pendingCount})
          </Button>
        )}
        {anyFilterActive && (
          <Button size="small" onClick={clearFilters}>Clear filters</Button>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {filtered.length} of {people.length}
        </Typography>
      </Stack>

      {/* List */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Qualifications</TableCell>
                <TableCell>Shelter Stone role</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Town</TableCell>
                <TableCell>Future events</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(p => (
                <TableRow hover key={p.id} onClick={() => setEditing(p.id)}>
                  <TableCell>
                    {p.forename} {p.surname}
                    {pendingById[p.id] && (
                      <Tooltip title={`Changed by them on ${fmtDateTime(pendingById[p.id].last_changed_at)} — awaiting review`}>
                        <Chip label="Changed" size="small" color="info" sx={{ ml: 1 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{p.status || "—"}</TableCell>
                  <TableCell>
                    {qualLabels(p).length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {qualLabels(p).map((l, i) => (
                          <Chip key={i} label={l} size="small" variant="outlined" />
                        ))}
                      </Box>
                    ) : "—"}
                  </TableCell>
                  <TableCell>
                    {(p.roles && p.roles.length > 0) ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {p.roles.map((r, i) => (
                          <Chip key={i} label={r} size="small" variant="outlined" />
                        ))}
                      </Box>
                    ) : "—"}
                  </TableCell>
                  <TableCell>{p.email || "—"}</TableCell>
                  <TableCell>{p.phone || "—"}</TableCell>
                  <TableCell>{p.town || "—"}</TableCell>
                  <TableCell>{futureEventCell(p.id)}</TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {loading ? <CircularProgress size={28} />
          : people.length === 0 ? "No personnel yet — add someone." : "No personnel match your filter."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / edit dialog — shared with Compliance and an event's personnel list */}
      <PersonnelDialog
        personId={editing}
        onClose={() => { setEditing(null); loadAllAttendances(); loadPending(); }}
        onSaved={upsertPerson}
        onDeleted={id => setPeople(ps => ps.filter(p => p.id !== id))} />
    </Stack>
  );
}
